/**
 * Deep-merge a tenant's `brand_overrides` (JSONB from the backend) on top of
 * a named `BrandConfig`. Returns a new object — does not mutate either input.
 *
 * Merge rules:
 *   - Top-level scalar / asset fields → overridden if present on overrides.
 *   - `colors`, `fonts`, `lightMode`, `darkMode` → shallow-merged (per key).
 *   - `loginFeatures`, `agentColors` → replaced wholesale when present
 *     (these are lists; merging by index is rarely what the user wants).
 *   - `agentAvatars` → shallow-merged; its `textures` list replaces wholesale,
 *     so an explicit `[]` turns a base brand's sets off, and its `mark`
 *     replaces the base brand's wholesale.
 *   - `null` / `undefined` overrides are ignored (don't blank out base brand).
 */

import type { BrandConfig, BrandOverrides } from './types';

export function mergeBrandOverrides(
  base: BrandConfig,
  overrides: BrandOverrides | null | undefined,
): BrandConfig {
  if (!overrides) return base;

  const next: BrandConfig = { ...base };

  // Scalars + assets
  const scalarKeys: (keyof BrandOverrides)[] = [
    'name', 'tagline', 'companyName', 'companyUrl', 'pageTitle', 'appName',
    'loginEyebrow',
    'logo', 'logoDark', 'icon', 'iconDark',
    'favicon', 'conciergeIcon', 'conciergeIconDark',
    'anniversaryIcon', 'anniversaryIconDark', 'anniversaryLogo', 'anniversaryLogoDark',
  ];
  for (const k of scalarKeys) {
    const v = overrides[k];
    if (v != null && v !== '') (next as unknown as Record<string, unknown>)[k] = v;
  }

  // Nested objects — shallow merge each
  if (overrides.colors) {
    next.colors = { ...base.colors, ...overrides.colors };

    // The primary CTA (Sign-In button, agent coins, primary buttons) is painted
    // from `gradientPrimary`, NOT from `primary`. A tenant that sets only
    // Primary — by far the common case — therefore kept the base brand's blue
    // gradient on every CTA while the rest of the page turned their colour,
    // with nothing on screen explaining why. Derive the gradient from the
    // tenant's own primary/primaryHover whenever they have not written an
    // explicit one, so "set your brand colour" does what it says. An explicit
    // gradientPrimary override always wins, and a tenant that overrides
    // neither keeps the base brand untouched.
    const setsGradient = overrides.colors.gradientPrimary != null
      && overrides.colors.gradientPrimary !== '';
    const setsPrimary = (overrides.colors.primary != null && overrides.colors.primary !== '')
      || (overrides.colors.primaryHover != null && overrides.colors.primaryHover !== '');
    if (!setsGradient && setsPrimary) {
      // Only the tenant's OWN hover colour may form the second stop. Falling
      // back to the base brand's hover would fade their colour into the
      // platform blue — the red-to-blue button that made this bug visible.
      const from = next.colors.primary;
      const ownHover = overrides.colors.primaryHover;
      const to = (ownHover != null && ownHover !== '') ? ownHover : next.colors.primary;
      next.colors.gradientPrimary = `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
    }
  }
  if (overrides.fonts) {
    next.fonts = { ...(base.fonts ?? { heading: '', body: '', mono: '' }), ...overrides.fonts };
  }
  if (overrides.lightMode) {
    next.lightMode = { ...(base.lightMode ?? defaultMode()), ...overrides.lightMode };
  }
  if (overrides.darkMode) {
    next.darkMode = { ...(base.darkMode ?? defaultMode()), ...overrides.darkMode };
  }

  // Lists — replace wholesale
  if (Array.isArray(overrides.loginFeatures)) next.loginFeatures = overrides.loginFeatures;
  if (Array.isArray(overrides.agentColors)) next.agentColors = overrides.agentColors;

  // Agent avatar templates — the object merges per key, and `textures` (an
  // ordered list) replaces wholesale, so `{ textures: [] }` is "no sets" even
  // over a preset that has them.
  if (overrides.agentAvatars) {
    next.agentAvatars = { ...(base.agentAvatars ?? {}), ...overrides.agentAvatars };
  }

  // Terminology / greeting copy — shallow-merge per key so overriding one
  // phrase doesn't drop the rest (the unset ones fall back to i18n).
  if (overrides.terminology) {
    next.terminology = { ...(base.terminology ?? {}), ...overrides.terminology };
  }

  // Visual treatment toggles — shallow-merge per nested object so
  // partial overrides ({ enabled: true } without color) keep the base
  // brand's color, and vice versa.
  if (overrides.homeSemicircle) {
    next.homeSemicircle = { ...(base.homeSemicircle ?? {}), ...overrides.homeSemicircle };
  }
  if (overrides.chat) {
    next.chat = { ...(base.chat ?? {}), ...overrides.chat };
  }

  // Transactional email branding — shallow-merge per key so overriding the
  // subject doesn't drop a custom sender name (unset keys fall back to the
  // general brand values on the backend).
  if (overrides.email) {
    next.email = { ...(base.email ?? {}), ...overrides.email };
  }

  return next;
}

function defaultMode() {
  return { bgPrimary: '', bgSecondary: '', bgTertiary: '', bgElevated: '' };
}
